/**
 * Australian report date range presets (Region Pack)
 */

import {
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subMonths,
  subQuarters,
  subYears,
} from 'date-fns';
import type { ReportDateRange, ReportDateRangePreset } from '../../../../types/report.types';

export interface ReportPeriodOptions {
  /** Calendar month the financial year starts (1–12). Default 7 (July, AU). */
  financialYearStartMonth?: number;
}

function getFinancialYearStartYear(date: Date, financialYearStartMonth: number): number {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  if (month < financialYearStartMonth) return year - 1;
  return year;
}

function getFinancialYearEndDate(startYear: number, financialYearStartMonth: number): Date {
  const nextFyStart = new Date(startYear + 1, financialYearStartMonth - 1, 1);
  nextFyStart.setDate(nextFyStart.getDate() - 1);
  return nextFyStart;
}

function toIsoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

function formatFyLabel(startYear: number): string {
  const endShort = (startYear + 1).toString().slice(-2);
  return `Financial Year ${startYear}–${endShort}`;
}

function getCurrentFyStart(reference: Date, financialYearStartMonth: number): Date {
  const fyStartYear = getFinancialYearStartYear(reference, financialYearStartMonth);
  return new Date(fyStartYear, financialYearStartMonth - 1, 1);
}

function getCurrentFyEnd(reference: Date, financialYearStartMonth: number): Date {
  const fyStartYear = getFinancialYearStartYear(reference, financialYearStartMonth);
  return getFinancialYearEndDate(fyStartYear, financialYearStartMonth);
}

export function resolveReportDateRange(
  preset: ReportDateRangePreset,
  customStart?: string,
  customEnd?: string,
  referenceDate: Date = new Date(),
  options?: ReportPeriodOptions
): ReportDateRange {
  const fyStartMonth = options?.financialYearStartMonth ?? 7;
  if (preset === 'custom') {
    if (!customStart || !customEnd) {
      throw new Error('Custom date range requires start and end dates');
    }
    if (customEnd < customStart) {
      throw new Error('End date must be on or after start date');
    }
    return {
      preset,
      startDate: customStart,
      endDate: customEnd,
      label: `${format(parseISO(customStart), 'd MMM yyyy')} – ${format(parseISO(customEnd), 'd MMM yyyy')}`,
    };
  }

  if (preset === 'this_month') {
    const start = startOfMonth(referenceDate);
    const end = endOfMonth(referenceDate);
    return {
      preset,
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
      label: `This Month (${format(start, 'MMMM yyyy')})`,
    };
  }

  if (preset === 'last_month') {
    const anchor = subMonths(referenceDate, 1);
    const start = startOfMonth(anchor);
    const end = endOfMonth(anchor);
    return {
      preset,
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
      label: `Last Month (${format(start, 'MMMM yyyy')})`,
    };
  }

  if (preset === 'this_quarter') {
    const start = startOfQuarter(referenceDate);
    const end = endOfQuarter(referenceDate);
    return {
      preset,
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
      label: `This Quarter (${format(start, 'MMM')} – ${format(end, 'MMM yyyy')})`,
    };
  }

  if (preset === 'last_quarter') {
    const anchor = subQuarters(referenceDate, 1);
    const start = startOfQuarter(anchor);
    const end = endOfQuarter(anchor);
    return {
      preset,
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
      label: `Last Quarter (${format(start, 'MMM')} – ${format(end, 'MMM yyyy')})`,
    };
  }

  if (preset === 'this_calendar_year') {
    const start = startOfYear(referenceDate);
    const end = referenceDate;
    return {
      preset,
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
      label: `This Calendar Year (${format(start, 'yyyy')})`,
    };
  }

  if (preset === 'last_calendar_year') {
    const anchor = subYears(referenceDate, 1);
    const start = startOfYear(anchor);
    const end = endOfYear(anchor);
    return {
      preset,
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
      label: `Last Calendar Year (${format(start, 'yyyy')})`,
    };
  }

  if (preset === 'this_financial_year') {
    const start = getCurrentFyStart(referenceDate, fyStartMonth);
    const fyEnd = getCurrentFyEnd(referenceDate, fyStartMonth);
    const end = referenceDate < fyEnd ? referenceDate : fyEnd;
    const fyStartYear = getFinancialYearStartYear(referenceDate, fyStartMonth);
    return {
      preset,
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
      label: formatFyLabel(fyStartYear),
    };
  }

  if (preset === 'last_financial_year') {
    const thisFyStartYear = getFinancialYearStartYear(referenceDate, fyStartMonth);
    const fyStartYear = thisFyStartYear - 1;
    const start = new Date(fyStartYear, fyStartMonth - 1, 1);
    const end = getFinancialYearEndDate(fyStartYear, fyStartMonth);
    return {
      preset,
      startDate: toIsoDate(start),
      endDate: toIsoDate(end),
      label: formatFyLabel(fyStartYear),
    };
  }

  throw new Error(`Unknown date range preset: ${preset}`);
}
